import { Link } from "react-router-dom";
import { PLANS } from "@/pages/Pricing";
import { Check, ArrowRight, Wallet, Receipt, Sparkles, FileText, Mic, ShieldCheck } from "lucide-react";

const PRODUCT_FEATURES = [
  { icon: Wallet, title: "Every rupee, one ledger", desc: "Income, expenses, budgets, accounts and demat holdings — tracked in ₹ on a single page your whole household can read." },
  { icon: Receipt, title: "Bills that pay & book themselves", desc: "Recurring bills auto-generate on due dates. Pay via Razorpay inside the app and the expense is recorded instantly." },
  { icon: Mic, title: "Speak, and it's logged", desc: "Say \"log 500 for sabzi yesterday\" — voice entries become clean, categorised transactions in seconds." },
  { icon: Sparkles, title: "An AI that knows your money", desc: "Ask anything about your spending in plain language. Get insights, overspend warnings and a weekly digest by email." },
  { icon: FileText, title: "Accountant-grade statements", desc: "Profit & Loss, Balance Sheet, Trial Balance and a full double-entry journal — ready for tax season." },
  { icon: ShieldCheck, title: "Private & secure", desc: "Bank-grade encryption in transit, secure cookie sessions, and your data stays yours — export to CSV anytime." },
];

function ProductNav() {
  return (
    <header className="sticky top-0 z-50 bg-[#F7F5F0]/95 backdrop-blur border-b-2 border-[#1A1F16]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
        <Link to="/" className="font-serif text-2xl lg:text-3xl tracking-tight shrink-0" data-testid="store-brand">
          Fin<span className="italic text-[#A67C00]">·</span>Ledger
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          {[["#features", "Features"], ["#pricing", "Pricing"]].map(([href, label]) => (
            <a key={href} href={href} data-testid={`store-nav-${label.toLowerCase()}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6C7565] hover:text-[#1A1F16] transition-colors">{label}</a>
          ))}
        </nav>
        <Link
          to="/register"
          data-testid="store-nav-get-started"
          className="bg-[#1A1F16] text-[#F7F5F0] px-4 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-[#2C3628] transition-colors shrink-0"
        >
          Get started →
        </Link>
      </div>
    </header>
  );
}

function ProductHero() {
  return (
    <section className="border-b-2 border-[#1A1F16]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A67C00]">The Product</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 max-w-3xl" data-testid="store-hero-title">
          FinLedger — the household finance dashboard, written like a ledger.
        </h1>
        <p className="text-base text-[#4A5142] mt-6 max-w-2xl leading-relaxed">
          Track income and expenses, plan budgets, pay bills, and let AI read your money —
          all in Indian Rupees, from one beautifully kept book.
        </p>
        <div className="mt-10 flex items-center gap-4 flex-wrap">
          <Link to="/register" data-testid="store-hero-cta" className="bg-[#1A1F16] text-[#F7F5F0] px-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] transition-colors flex items-center gap-2">
            Open your ledger <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <a href="#pricing" className="border border-[#1A1F16] px-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] transition-colors">
            See pricing
          </a>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] mt-6">Free forever plan · 7-day Premium trial · No card required</p>
      </div>
    </section>
  );
}

function ProductFeatures() {
  return (
    <section id="features" className="border-b-2 border-[#1A1F16] bg-white">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">What's inside</span>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-12">Everything a household ledger should do.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#D8D4C7]">
          {PRODUCT_FEATURES.map((f) => (
            <div key={f.title} className="border-r border-b border-[#D8D4C7] p-8" data-testid={`store-feature-${f.title.split(" ")[0].toLowerCase().replace(",", "")}`}>
              <f.icon className="w-5 h-5 text-[#A67C00]" />
              <h3 className="font-serif text-2xl tracking-tight mt-4">{f.title}</h3>
              <p className="text-sm text-[#4A5142] leading-relaxed mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductPricing() {
  return (
    <section id="pricing" className="border-b-2 border-[#1A1F16]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Pricing</span>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-12">Honest prices, in rupees.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D8D4C7] border border-[#D8D4C7]">
          {PLANS.map((p) => (
            <div key={p.id} className="bg-white p-8 flex flex-col" data-testid={`store-plan-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6C7565]">{p.name}</span>
                {p.badge && <span className="bg-[#A67C00] text-white font-mono text-[9px] uppercase tracking-widest px-2 py-1">{p.badge}</span>}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif text-5xl tracking-tight num">{p.price}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{p.period}</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4A5142]">
                    <Check className="w-3.5 h-3.5 text-[#2E6F40] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to={p.id === "free" ? "/register" : "/pricing"}
                data-testid={`store-plan-cta-${p.id}`}
                className="mt-8 text-center bg-[#1A1F16] text-[#F7F5F0] px-5 py-3.5 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] transition-colors"
              >
                {p.id === "free" ? "Start free" : "Choose plan"}
              </Link>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#6C7565] mt-6">Payments are processed securely by Razorpay. Premium plans unlock instantly after payment.</p>
      </div>
    </section>
  );
}

function ProductFooter() {
  return (
    <footer className="bg-[#1A1F16] text-[#EAE7DD]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <div className="font-serif text-2xl tracking-tight">Fin<span className="italic text-[#A67C00]">·</span>Ledger</div>
          <div className="text-xs text-[#A8AFA0] mt-3" data-testid="store-footer-attribution">
            FinLedger is a product of <span className="text-[#A67C00]">SS Ayurvedam Digital Solutions</span>.
          </div>
        </div>
        <div className="flex gap-6 font-mono text-[10px] uppercase tracking-[0.2em]">
          <Link to="/" className="hover:text-[#A67C00] transition-colors" data-testid="store-footer-home">Home</Link>
          <Link to="/login" className="hover:text-[#A67C00] transition-colors" data-testid="store-footer-signin">Sign in</Link>
          <Link to="/register" className="hover:text-[#A67C00] transition-colors" data-testid="store-footer-register">Get started</Link>
        </div>
      </div>
    </footer>
  );
}

export default function StoreHome() {
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1A1F16] paper-grain" data-testid="store-page">
      <ProductNav />
      <main>
        <ProductHero />
        <ProductFeatures />
        <ProductPricing />
      </main>
      <ProductFooter />
    </div>
  );
}
